import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { colors } from "../theme/theme";
import SidebarMenuItem from "../components/Sidebar/SidebarMenuItem";
import {
  KeyboardArrowRightOutlined,
  ChevronLeft,
  ChevronRight,
  Dashboard,
  DashboardOutlined,
  DateRange,
  DateRangeOutlined,
  Forum,
  ForumOutlined,
  Groups,
  GroupsOutlined,
  Chat,
  ChatOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  clearUserData,
  setAuthenticationState,
} from "../redux/dispatchers/users.dispatcher";
import MobileSidebar from "../components/Sidebar/MobileSidebar";

const FloatingSidebar = ({ open, toggleSidebar, hideSideBar }) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);

  const mdScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const smScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  const isProfilePage = pathnames.includes("me");

  const sidebarWidth = open ? 260 : 80;

  useEffect(() => {
    if (open && hideSideBar) {
      toggleSidebar(false);
    }
  }, [open, hideSideBar, toggleSidebar]);

  useEffect(() => {
    if (mdScreen) {
      toggleSidebar(false);
    }
  }, [mdScreen, toggleSidebar]);

  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { upcomingEvents } = useSelector((state) => state.events);
  const { allForums } = useSelector((state) => state.forums);
  const { allGroups } = useSelector((state) => state.groups);
  const { conversations } = useSelector((state) => state.chats);

  const sidebarData = [
    {
      id: 1,
      title: "Dashboard",
      FocusedIcon: Dashboard,
      Icon: DashboardOutlined,
      route: "/dashboard",
      noCount: true,
    },
    {
      id: 2,
      title: "Events",
      FocusedIcon: DateRange,
      Icon: DateRangeOutlined,
      count: upcomingEvents?.length || 0,
      route: "/events",
    },
    {
      id: 3,
      title: "Forum",
      FocusedIcon: Forum,
      Icon: ForumOutlined,
      count: allForums?.length || 0,
      route: "/forum",
    },
    {
      id: 4,
      title: "Groups",
      FocusedIcon: Groups,
      Icon: GroupsOutlined,
      count: allGroups?.length || 0,
      route: "/groups",
    },
    {
      id: 5,
      title: "Messages",
      FocusedIcon: Chat,
      Icon: ChatOutlined,
      count: conversations?.length || 0,
      route: "/messages",
    },
  ];

  return (
    <>
      {!smScreen && (
        <Box
          sx={{
            position: "fixed",
            top: 20,
            left: 20,
            height: "calc(100% - 40px)",
            width: sidebarWidth,
            backgroundColor: "#fff",
            borderRadius: "20px",
            boxShadow: "10px 0 20px rgba(0,0,0,0.3)",
            transition: "width 0.3s ease",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            zIndex: hideSideBar ? 1 : 1300,
          }}
        >
          <Box
            sx={{ m: 2, cursor: "pointer" }}
            onClick={() => navigate("/dashboard")}
          >
            <img
              src={
                mdScreen
                  ? require("../assets/icons/logo_mobile.png")
                  : require("../assets/icons/logo_sm.png")
              }
              style={{
                width: mdScreen ? "50px" : "170px",
                height: "50px",
              }}
              alt="logo_sm"
            />
          </Box>

          <SidebarMenuItem open={open} sidebarData={sidebarData} />

          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                backgroundColor: isProfilePage
                  ? colors.secondary
                  : "rgba(247,132,25,0.14)",
                p: open ? 1 : 0,
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: open ? "space-between" : "center",
                borderRadius: "9px",
                gap: 1,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(247,132,25,0.3)",
                },
              }}
              onClick={() => navigate("/profile/me")}
            >
              {open ? (
                <>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <img
                      src={
                        userData?.avatar ||
                        require("../assets/images/default.jpg")
                      }
                      alt="avatar"
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                      }}
                    />
                    <Stack direction="column">
                      <Typography
                        sx={{
                          fontFamily: "Mulish-Bold",
                          color: colors.primary,
                          fontSize: 14,
                          lineHeight: 1.5,
                        }}
                      >
                        {userData?.firstName + " " + userData?.lastName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Mulish-Regular",
                          color: colors.primary,
                          fontSize: 10,
                          lineHeight: 1.5,
                        }}
                      >
                        Membership since{" "}
                        {" " + dayjs(userData?.createdAt).format("YYYY")}
                      </Typography>
                    </Stack>
                  </Stack>
                  <IconButton
                    sx={{
                      color: "#fff",
                      borderRadius: "50%",
                      width: "30px",
                      height: "30px",
                      "&:hover": {
                        backgroundColor: colors.primary + "99",
                      },
                    }}
                    onClick={handleClick}
                  >
                    <KeyboardArrowRightOutlined
                      sx={{ fontSize: 24, color: colors.primary }}
                    />
                  </IconButton>
                </>
              ) : (
                <img
                  src={require("../assets/images/default.jpg")}
                  alt="avatar"
                  style={{ width: "40px", height: "40px", borderRadius: "50%" }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
      {!mdScreen && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 50,
            left: open ? 260 + 5 - 12 : 80 + 5 - 12,
            zIndex: hideSideBar ? 1 : 1300,
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: "#fff",
            border: "0.5px solid #192335",
            transform: "translateX(50%)",
          }}
        >
          <IconButton
            onClick={() => toggleSidebar(!open)}
            sx={{ color: "#fff" }}
          >
            {open ? (
              <ChevronLeft sx={{ color: colors.primary }} />
            ) : (
              <ChevronRight sx={{ color: colors.primary }} />
            )}
          </IconButton>
        </Box>
      )}

      {smScreen && (
        <MobileSidebar
          open={open}
          toggleSidebar={toggleSidebar}
          sidebarData={sidebarData}
          isProfilePage={isProfilePage}
        />
      )}

      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            dispatch(setAuthenticationState(false));
            dispatch(clearUserData());
          }}
          sx={{ fontFamily: "Mulish-SemiBold", fontSize: "14px" }}
        >
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default FloatingSidebar;
